import { useCallback, useEffect } from 'react';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import { useDispatch } from 'react-redux';
import {
  selectComment,
  setCanvasMode,
  setDrawToolsShape
} from '@redux/actions/mediaViewerAction';
import { useAuth } from '@hooks/useAuth';

export default function useMediaViewerTextHighlightTour({
  isAssetReady,
  isDocument,
  isPreparing,
  isUnsupported
}: {
  isAssetReady: boolean;
  isDocument: boolean;
  isPreparing: boolean;
  isUnsupported: boolean;
}) {
  const dispatch = useDispatch();
  const user = useAuth(false);
  const { onStartTour, tourData, onCompleteStep } = useOnboardingProcess();

  const completedOnboardingSteps = user?.user?.completedOnboardingSteps || [];

  const onStartTextHighlight = useCallback(() => {
    onStartTour({
      type: 'text-highlight',
      onClickNext: (currentStep, isExitTour) => {
        if (isExitTour) {
          onCompleteStep({ type: 'text-highlight', hideCongrats: true });
          return;
        }
        if (currentStep === 0) {
          dispatch(selectComment('new'));
          dispatch(setCanvasMode('edit'));
          dispatch(setDrawToolsShape('documentHighlight'));
        }
      },
      onClickPrev: (currentStep, isExitTour) => {
        if (isExitTour) {
          onCompleteStep({ type: 'text-highlight', hideCongrats: true });
          return;
        }
        if (currentStep === 1) {
          dispatch(setCanvasMode('view'));
          return;
        }
        if (currentStep === 2) {
          dispatch(setCanvasMode('edit'));
        }
      }
    });
  }, [onStartTour, onCompleteStep, dispatch]);

  useEffect(() => {
    if (user?.user?.type !== 'internal') return;
    if (tourData?.type) return;
    if (completedOnboardingSteps.includes('text-highlight')) return;
    if (!isDocument || isUnsupported) return;
    if (isPreparing) return;
    if (!isAssetReady) return;
    onStartTextHighlight();
  }, [
    isAssetReady,
    isDocument,
    isPreparing,
    isUnsupported,
    completedOnboardingSteps,
    user?.user?.type,
    tourData?.type,
    onStartTextHighlight
  ]);

  return null;
}
